import React, { useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

/**
 * Landing page with Lusqtoff look & feel + project search and chat bot
 * Replace placeholder images (src="/img/...") with real assets.
 * The chat sidebar is a simple front‑end shell; connect it to your backend
 * by replacing the TODOs.
 */
export default function LandingPage() {
    /* ------------------ state ------------------ */
    const [query, setQuery] = useState("");
    const [showChat, setShowChat] = useState(false);
    const [messages, setMessages] = useState([]); // {author: "user"|"bot", text: string}
    const [inputMsg, setInputMsg] = useState("");
    const endOfMsgsRef = useRef(null);

    /* ------------------ handlers ------------------ */
    const handleSearch = (e) => {
        e.preventDefault();
        if (!query.trim()) return;
        setShowChat(true);
        setMessages((prev) => [
            ...prev,
            { author: "user", text: query.trim() },
            {
                author: "bot",
                text: "¡Hola! Ahora te recomiendo algunas herramientas… (ejemplo).",
            },
        ]);
        setInputMsg("");
        // TODO: send "query" to your chat‑bot backend and append bot replies.
    };

    const sendMessage = (e) => {
        e.preventDefault();
        if (!inputMsg.trim()) return;
        setMessages((prev) => [...prev, { author: "user", text: inputMsg.trim() }]);
        // TODO: send inputMsg to backend and append bot reply when you receive it.
        setInputMsg("");
    };

    /* ------------------ scroll to bottom ------------------ */
    useEffect(() => {
        endOfMsgsRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, showChat]);

    /* ------------------ dummy products ------------------ */
    const featuredProducts = [
        { id: 1, name: "Amoladora 115mm", img: "/img/amoladora.jpg" },
        { id: 2, name: "Taladro Percutor", img: "/img/taladro.jpg" },
        { id: 3, name: "Martillo Demoledor", img: "/img/martillo.jpg" },
        { id: 4, name: "Hidrolavadora", img: "/img/hidrolavadora.jpg" },
    ];

    /* ------------------ component ------------------ */
    return (
        <div className="font-sans text-gray-900">
            {/* Chat sidebar */}
            <div
                className={`fixed top-0 right-0 h-full w-full sm:max-w-sm bg-white shadow-2xl z-[60] transform transition-transform duration-300 ${showChat ? "translate-x-0" : "translate-x-full"}
        flex flex-col`}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-[#ff6b00] text-white">
                    <span className="font-semibold">Asistente Lusqtoff</span>
                    <button
                        onClick={() => setShowChat(false)}
                        className="text-2xl leading-none focus:outline-none"
                    >
                        ×
                    </button>
                </div>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2 bg-gray-50">
                    {messages.map((m, idx) => (
                        <div
                            key={idx}
                            className={`max-w-[80%] px-4 py-2 rounded-lg text-sm ${m.author === "user"
                                    ? "ml-auto bg-[#ff6b00] text-white"
                                    : "mr-auto bg-white border"}`}
                        >
                            {m.text}
                        </div>
                    ))}
                    <div ref={endOfMsgsRef} />
                </div>
                {/* Input */}
                <form onSubmit={sendMessage} className="p-3 flex gap-2 border-t">
                    <input
                        type="text"
                        value={inputMsg}
                        onChange={(e) => setInputMsg(e.target.value)}
                        placeholder="Escribí tu pregunta…"
                        className="flex-1 rounded-full border px-4 py-2 text-sm focus:outline-none"
                    />
                    <button
                        type="submit"
                        className="bg-[#ff6b00] text-white font-semibold px-4 py-2 rounded-full"
                    >
                        ➤
                    </button>
                </form>
            </div>

            {/* Backdrop when chat open */}
            {showChat && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 sm:z-40"
                    onClick={() => setShowChat(false)}
                />
            )}

            {/* Main content below keeps same scroll context */}

            {/* Header */}
            <header className="sticky top-0 z-30 flex items-center justify-between bg-white shadow px-6 h-16">
                <div className="flex items-center space-x-2">
                    <img src="/img/lusqtoff-logo.svg" alt="Lusqtoff" className="h-8" />
                </div>
                <nav className="hidden md:flex space-x-6 font-medium">
                    <a href="#productos" className="hover:text-[#ff6b00] transition-colors">Productos</a>
                    <a href="#beneficios" className="hover:text-[#ff6b00] transition-colors">Beneficios</a>
                    <a href="#contacto" className="hover:text-[#ff6b00] transition-colors">Contacto</a>
                </nav>
                <a
                    href="#catalogo"
                    className="bg-[#ff6b00] text-white py-2 px-4 rounded-lg shadow hover:shadow-lg transition-shadow"
                >
                    Ver Catálogo
                </a>
            </header>

            {/* Hero */}
            <section
                className="relative flex flex-col items-center justify-center h-[70vh] bg-center bg-cover"
                style={{ backgroundImage: "url(/img/hero-lusqtoff.jpg)" }}
            >
                <div className="absolute inset-0 bg-black/50" />
                <div className="relative z-10 text-center text-white px-4 w-full max-w-2xl">
                    <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4 drop-shadow-xl">
                        Potencia que transforma tu trabajo
                    </h1>
                    <p className="text-lg md:text-2xl mb-6">¿Qué tenés pensado hacer?</p>
                    <form onSubmit={handleSearch} className="flex gap-2 justify-center">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Ej. cortar metal, pulir madera…"
                            className="w-full md:w-2/3 py-3 px-5 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none"
                        />
                        <button
                            type="submit"
                            className="bg-[#ff6b00] hover:bg-white hover:text-[#ff6b00] transition-colors font-semibold py-3 px-6 rounded-full shadow-lg"
                        >
                            Buscar
                        </button>
                    </form>
                </div>
            </section>

            {/* Productos Destacados */}
            <section id="productos" className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-10">Productos destacados</h2>
                    <Swiper
                        modules={[Navigation, Autoplay]}
                        spaceBetween={24}
                        slidesPerView={1}
                        navigation
                        autoplay={{ delay: 3500 }}
                        breakpoints={{ 640: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
                        className="select-none"
                    >
                        {featuredProducts.map((product) => (
                            <SwiperSlide key={product.id}>
                                <div className="bg-white rounded-2xl shadow hover:shadow-xl transition-shadow overflow-hidden">
                                    <img src={product.img} alt={product.name} className="h-52 w-full object-cover" />
                                    <div className="p-4 flex justify-between items-center">
                                        <span className="font-medium">{product.name}</span>
                                        <button className="bg-[#ff6b00] text-white text-sm py-1 px-3 rounded-lg">Comprar</button>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </section>

            {/* Beneficios */}
            <section id="beneficios" className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-3 gap-8 text-center">
                    {[{
                        title: "Construidas para durar",
                        desc: "Materiales de alta resistencia y motores de alto rendimiento.",
                        icon: "/img/icon-durability.svg",
                    }, {
                        title: "Garantía Lusqtoff",
                        desc: "Respaldo oficial y servicio postventa en todo el país.",
                        icon: "/img/icon-warranty.svg",
                    }, {
                        title: "Envíos a todo Argentina",
                        desc: "Recibí tus herramientas en 24-72 hs con nuestra red logística.",
                        icon: "/img/icon-shipping.svg",
                    }].map((b, i) => (
                        <div key={i} className="flex flex-col items-center p-6 rounded-2xl shadow hover:shadow-md transition-shadow">
                            <img src={b.icon} alt="icon" className="h-16 mb-4" />
                            <h3 className="text-xl font-semibold mb-2">{b.title}</h3>
                            <p className="text-gray-600 leading-relaxed">{b.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Call to Action */}
            <section id="catalogo" className="py-20 bg-[#ff6b00] text-white text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Descargá nuestro catálogo completo</h2>
                <p className="mb-8 max-w-2xl mx-auto text-lg">
                    Encontrá la herramienta perfecta para cada desafío y llevá tu trabajo al siguiente nivel.
                </p>
                <a
                    href="/catalogo-lusqtoff.pdf"
                    className="bg-white text-[#ff6b00] font-semibold py-3 px-10 rounded-full shadow hover:shadow-lg transition-shadow"
                    download
                >
                    Descargar PDF
                </a>
            </section>

            {/* Contacto */}
            <section id="contacto" className="py-16 bg-gray-900 text-white">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold mb-6">¿Necesitás asesoramiento?</h2>
                    <p className="mb-8 text-gray-300">
                        Escribinos y un especialista te ayudará a elegir la herramienta indicada
                        para tu proyecto.
                    </p>
                    <form className="grid gap-4 md:grid-cols-2">
                        <input
                            type="text"
                            placeholder="Nombre"
                            className="py-3 px-4 rounded-lg bg-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#ff6b00] md:col-span-1"
                        />
                        <input
                            type="email"
                            placeholder="Email"
                            className="py-3 px-4 rounded-lg bg-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#ff6b00] md:col-span-1"
                        />
                        <textarea
                            placeholder="Mensaje"
                            rows={4}
                            className="py-3 px-4 rounded-lg bg-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#ff6b00] md:col-span-2"
                        />
                        <button
                            type="submit"
                            className="bg-[#ff6b00] hover:bg-white hover:text-[#ff6b00] transition-colors font-semibold py-3 px-10 rounded-full md:col-span-2"
                        >
                            Enviar mensaje
                        </button>
                    </form>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-6 bg-black text-gray-400 text-center text-sm">
                © {new Date().getFullYear()} Lusqtoff. Todos los derechos reservados.
            </footer>
        </div>
    );
}
